// Custom Hook: Google Calendar Sync
// Ausgelagert im Rahmen des Phase-3-Refactorings (März 2026)
import { useState, useEffect } from 'react';
import { GOOGLE_CLIENT_ID, GOOGLE_SCOPES, STORAGE_KEYS } from '@/utils/calendarConstants';
import { loadData, saveData } from '@/utils/calendarUtils';

export default function useGoogleCalendarSync({ appointments, setAppointments }) {
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [googleCalendars, setGoogleCalendars] = useState(() => loadData(STORAGE_KEYS.googleCalendars, []));
  const [selectedCalendars, setSelectedCalendars] = useState(() => {
    const saved = loadData(STORAGE_KEYS.googleCalendars, []);
    return saved.map(c => c.id);
  });
  const [primaryCalendar, setPrimaryCalendar] = useState(() => loadData('zielplanung_primary_calendar', null));
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncedAppointments, setSyncedAppointments] = useState(() => loadData(STORAGE_KEYS.syncedAppointments, []));

  // Google OAuth - initiate connection
  const initiateGoogleAuth = () => {
    const redirectUri = window.location.origin + '/zielplanung';
    const params = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'token',
      scope: GOOGLE_SCOPES,
      prompt: 'consent',
      include_granted_scopes: 'true'
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  };

  // Handle OAuth callback (check URL for access token)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('access_token')) {
      const params = new URLSearchParams(hash.substring(1));
      const accessToken = params.get('access_token');
      if (accessToken) {
        window.history.replaceState(null, '', window.location.pathname);
        fetchGoogleCalendars(accessToken);
        sessionStorage.setItem('google_access_token', accessToken);
      }
    }
  }, []);

  // Fetch user's Google calendars
  const fetchGoogleCalendars = async (accessToken) => {
    setSyncLoading(true);
    try {
      const response = await fetch(
        'https://www.googleapis.com/calendar/v3/users/me/calendarList',
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (response.ok) {
        const data = await response.json();
        const calendars = data.items || [];
        setGoogleCalendars(calendars);
        saveData(STORAGE_KEYS.googleCalendars, calendars);
        setSelectedCalendars(calendars.slice(0, 3).map(c => c.id));
        setShowSyncModal(true);
      } else {
        setSyncStatus({ success: false, message: 'Fehler beim Laden der Kalender' });
      }
    } catch (error) {
      setSyncStatus({ success: false, message: 'Verbindungsfehler' });
    } finally {
      setSyncLoading(false);
    }
  };

  // Sync events from Google Calendar
  const syncGoogleEvents = async () => {
    const accessToken = sessionStorage.getItem('google_access_token');
    if (!accessToken) { initiateGoogleAuth(); return; }

    setSyncLoading(true);
    setSyncStatus(null);

    try {
      const now = new Date();
      const monday = new Date(now);
      const dayOfWeek = monday.getDay();
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      monday.setDate(monday.getDate() - daysToMonday);
      monday.setHours(0, 0, 0, 0);
      const oneWeekAhead = new Date(monday);
      oneWeekAhead.setDate(oneWeekAhead.getDate() + 7);
      const timeMin = monday.toISOString();
      const timeMax = oneWeekAhead.toISOString();

      let allEvents = [];
      const exportedGoogleIds = appointments
        .filter(a => a.googleEventId && !a.isGoogleEvent)
        .map(a => a.googleEventId);

      for (const calendarId of selectedCalendars) {
        try {
          const response = await fetch(
            `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events?` +
            new URLSearchParams({ timeMin, timeMax, singleEvents: 'true', orderBy: 'startTime', maxResults: '500' }),
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );
          if (response.ok) {
            const data = await response.json();
            const events = (data.items || [])
              .filter(event => !exportedGoogleIds.includes(event.id))
              .map(event => ({
                id: `google_${event.id}`,
                googleId: event.id,
                calendarId,
                title: event.summary || 'Kein Titel',
                date: event.start.date || event.start.dateTime?.split('T')[0],
                startTime: event.start.dateTime ? event.start.dateTime.split('T')[1].substring(0, 5) : '00:00',
                endTime: event.end.dateTime ? event.end.dateTime.split('T')[1].substring(0, 5) : '23:59',
                isGoogleEvent: true,
                color: googleCalendars.find(c => c.id === calendarId)?.backgroundColor || '#10b981'
              }));
            allEvents = [...allEvents, ...events];
          }
        } catch (err) {
          console.error(`Error fetching events from ${calendarId}:`, err);
        }
      }

      setSyncedAppointments(allEvents);
      saveData(STORAGE_KEYS.syncedAppointments, allEvents);

      const existingNonGoogle = appointments.filter(a => !a.isGoogleEvent);
      const merged = [...existingNonGoogle, ...allEvents];
      setAppointments(merged);
      saveData(STORAGE_KEYS.appointments, merged);

      // Export local appointments to Google
      let exportedCount = 0;
      let exportErrors = 0;
      const targetCalendarId = primaryCalendar || 'primary';
      const appointmentsToExport = existingNonGoogle.filter(apt => {
        const aptDate = new Date(apt.date);
        return !apt.googleEventId && aptDate >= monday && aptDate <= oneWeekAhead;
      });

      for (const apt of appointmentsToExport) {
        try {
          const eventData = {
            summary: apt.title,
            start: { dateTime: `${apt.date}T${apt.startTime}:00`, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone },
            end: { dateTime: `${apt.date}T${apt.endTime}:00`, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }
          };
          const response = await fetch(
            `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(targetCalendarId)}/events`,
            { method: 'POST', headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' }, body: JSON.stringify(eventData) }
          );
          if (response.ok) {
            const createdEvent = await response.json();
            apt.googleEventId = createdEvent.id;
            apt.exportedToCalendar = targetCalendarId;
            exportedCount++;
          } else { exportErrors++; }
        } catch (err) { exportErrors++; }
      }

      if (exportedCount > 0) {
        const updatedAppointments = [...existingNonGoogle, ...allEvents];
        setAppointments(updatedAppointments);
        saveData(STORAGE_KEYS.appointments, updatedAppointments);
      }

      let statusMsg = `✅ ${allEvents.length} Termine von Google geholt`;
      if (exportedCount > 0) statusMsg += `, ${exportedCount} zu Google gesendet`;
      if (exportErrors > 0) statusMsg += ` (${exportErrors} Fehler)`;
      setSyncStatus({ success: exportErrors === 0, message: statusMsg });

    } catch (error) {
      setSyncStatus({ success: false, message: 'Synchronisierung fehlgeschlagen' });
    } finally {
      setSyncLoading(false);
    }
  };

  const deleteSyncedAppointments = () => {
    const nonGoogleAppointments = appointments.filter(a => !a.isGoogleEvent);
    setAppointments(nonGoogleAppointments);
    saveData(STORAGE_KEYS.appointments, nonGoogleAppointments);
    setSyncedAppointments([]);
    saveData(STORAGE_KEYS.syncedAppointments, []);
    setSyncStatus({ success: true, message: 'Synchronisierte Termine gelöscht' });
  };

  const handleCalendarToggle = (calendarId) => {
    setSelectedCalendars(prev =>
      prev.includes(calendarId) ? prev.filter(id => id !== calendarId) : [...prev, calendarId]
    );
  };

  const handlePrimaryCalendarChange = (calendarId) => {
    setPrimaryCalendar(calendarId);
    saveData('zielplanung_primary_calendar', calendarId);
  };

  const handleSyncClick = () => {
    const accessToken = sessionStorage.getItem('google_access_token');
    if (!accessToken) { initiateGoogleAuth(); return; }
    if (googleCalendars.length === 0) { fetchGoogleCalendars(accessToken); return; }
    if (!primaryCalendar) { setShowSyncModal(true); return; }
    syncGoogleEvents();
  };

  return {
    showSyncModal, setShowSyncModal,
    googleCalendars, selectedCalendars, primaryCalendar,
    syncLoading, syncStatus, syncedAppointments,
    handleSyncClick, handleCalendarToggle,
    handlePrimaryCalendarChange, deleteSyncedAppointments,
    syncGoogleEvents
  };
}
